import React from "react";
import { Link } from "react-router-dom";
import { FaShieldAlt, FaLock, FaCreditCard, FaUndo } from "react-icons/fa";

const PaymentPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full mb-4">
            <FaShieldAlt className="text-4xl text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Payment Policy
          </h1>
          <p className="text-xl text-gray-600">
            Secure, transparent, and customer-focused payment terms
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: January 2025
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
          {/* Security Section */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <FaLock className="text-2xl text-cyan-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Secure Payment Processing
              </h2>
            </div>
            <p className="text-gray-700 mb-4">
              At Sea Pulse Albania, we prioritize the security of your financial
              information. All payments are processed through industry-standard
              secure payment gateways with:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>256-bit SSL encryption for all transactions</li>
              <li>PCI DSS Level 1 compliance</li>
              <li>Secure card processing through verified payment providers</li>
              <li>
                No storage of complete credit card information on our servers
              </li>
            </ul>
          </div>

          {/* Accepted Payment Methods */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <FaCreditCard className="text-2xl text-cyan-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Accepted Payment Methods
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-2">
                  Credit & Debit Cards
                </h3>
                <p className="text-gray-700 text-sm">
                  Visa, Mastercard, American Express
                </p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-2">Online Banking</h3>
                <p className="text-gray-700 text-sm">
                  Direct bank transfers and e-wallets
                </p>
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Payment Terms
            </h2>
            <div className="space-y-6">
              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  1. Full Payment at Booking
                </h3>
                <p className="text-gray-700">
                  To confirm your reservation, full payment is required at the
                  time of booking. You will receive an instant confirmation
                  email with your booking details and receipt.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  2. Pricing Transparency
                </h3>
                <p className="text-gray-700">
                  All prices displayed on our website are final and include:
                </p>
                <ul className="list-disc list-inside mt-2 text-gray-700 ml-4">
                  <li>Boat rental for the specified duration</li>
                  <li>Professional skipper service</li>
                  <li>Fuel costs</li>
                  <li>Safety equipment (life jackets)</li>
                  <li>Insurance coverage</li>
                </ul>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  3. Additional Costs
                </h3>
                <p className="text-gray-700">
                  Optional extras (food, drinks, water sports equipment) are
                  available at an additional cost and can be added during
                  booking or on-site.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">4. Currency</h3>
                <p className="text-gray-700">
                  All prices are quoted in Euros (EUR). Multi-currency payment
                  options available with automatic conversion at current
                  exchange rates.
                </p>
              </div>
            </div>
          </div>

          {/* Cancellation & Refund Policy */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <FaUndo className="text-2xl text-cyan-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Cancellation & Refund Policy
              </h2>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-6">
              <p className="font-bold text-green-800 mb-2">
                100% Refund Guarantee with Proper Notice
              </p>
              <p className="text-gray-700">
                We offer a flexible cancellation policy to protect your
                investment.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                  ✓
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    Full Refund (100%)
                  </h3>
                  <p className="text-gray-700">
                    Cancellations made 48 hours or more before scheduled
                    departure
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                  50%
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    Partial Refund (50%)
                  </h3>
                  <p className="text-gray-700">
                    Cancellations made between 24-48 hours before departure
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                  ✗
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">No Refund</h3>
                  <p className="text-gray-700">
                    Cancellations made less than 24 hours before departure or
                    no-shows
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-2">
                Weather-Related Cancellations
              </h3>
              <p className="text-gray-700">
                If we cancel your tour due to unsafe weather conditions, you
                will receive:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-700 ml-4">
                <li>Full 100% refund, or</li>
                <li>Free rescheduling to any available date</li>
              </ul>
            </div>
          </div>

          {/* Refund Processing */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Refund Processing Time
            </h2>
            <p className="text-gray-700 mb-4">
              Approved refunds are processed within:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Credit/Debit cards: 5-10 business days</li>
              <li>Bank transfers: 3-7 business days</li>
              <li>E-wallets: 1-3 business days</li>
            </ul>
            <p className="text-gray-600 text-sm mt-4">
              * Processing times may vary depending on your financial
              institution
            </p>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Questions About Payments?
            </h3>
            <p className="text-gray-700 mb-4">
              Our customer support team is available to assist you with any
              payment-related queries.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:seapulsealbania@gmail.com"
                className="text-cyan-600 hover:text-cyan-700 font-medium"
              >
                seapulsealbania@gmail.com
              </a>
              <span className="text-gray-400">•</span>
              <a
                href="tel:+355692054408"
                className="text-cyan-600 hover:text-cyan-700 font-medium"
              >
                +355 69 205 4408
              </a>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="text-center">
          <div className="flex justify-center gap-6 text-gray-600">
            <Link
              to="/boat-tours"
              className="hover:text-cyan-600 transition-colors"
            >
              Browse Tours
            </Link>
            <span>•</span>
            <Link
              to="/terms-and-conditions"
              className="hover:text-cyan-600 transition-colors"
            >
              Terms & Conditions
            </Link>
            <span>•</span>
            <Link to="/about" className="hover:text-cyan-600 transition-colors">
              About Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPolicy;
